import React, { useState } from 'react';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../services/api';
import { useT } from '../services/i18n';
import { usePageMeta } from '../services/seo';

const LoginPage = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get('redirect');
  const sessionExpired = new URLSearchParams(location.search).get('expired') === '1';
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  usePageMeta({ title: t('auth.loginTitle'), description: t('auth.loginSub'), noindex: true });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.email) newErrors.email = t('auth.emailRequired');
    if (!formData.password) newErrors.password = t('auth.passwordRequired');
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsLoading(true);
    setApiError('');
    try {
      const response = await authAPI.login({ email: formData.email, password: formData.password, remember_me: formData.rememberMe });
      if (redirectTo && redirectTo.startsWith('/')) navigate(redirectTo);
      else if (response.user.is_admin) navigate('/admin');
      else navigate('/');
    } catch (error) {
      setApiError(error.response?.status === 401 || error.response?.status === 400 ? t('auth.badCredentials') : error.response?.data?.detail || t('auth.badCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4">
      <LanguageSwitcher variant="auth" />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block"><Logo variant="light" size="lg" className="mx-auto" /></Link>
          <h1 className="mt-4 text-2xl font-bold text-gray-900" data-testid="login-title">{t('auth.loginTitle')}</h1>
          <p className="mt-2 text-sm text-gray-600">{t('auth.loginSub')}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          {sessionExpired && !apiError && (
            <div data-testid="session-expired-notice" className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-md text-sm">{t('auth.sessionExpired')}</div>
          )}
          {apiError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm" data-testid="login-error">{apiError}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className={`pl-10 ${errors.email ? 'border-red-500' : ''}`} placeholder={t('auth.emailPh')} data-testid="login-email" />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`} placeholder={t('auth.passwordPh')} data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 inline-flex items-center justify-center w-9 h-9" aria-label={t('auth.showPassword')}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="rememberMe" data-testid="login-remember-me" checked={formData.rememberMe} onChange={handleChange} className="rounded border-gray-300 text-orange-500 focus:ring-orange-500" />
                <span className="text-sm text-gray-600">{t('auth.remember')} <span className="text-gray-400">{t('auth.rememberDays')}</span></span>
              </label>
              <Link to="/mot-de-passe-oublie" data-testid="forgot-password-link" className="text-sm text-blue-600 hover:underline">{t('auth.forgot')}</Link>
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 mt-6" disabled={isLoading} data-testid="login-submit">
              {isLoading ? t('auth.loggingIn') : t('auth.login')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('auth.noAccount')}{' '}
              <Link to={redirectTo ? `/inscription?redirect=${encodeURIComponent(redirectTo)}` : '/inscription'} className="text-blue-600 hover:underline font-medium inline-block py-2" data-testid="login-register-link">{t('auth.createAccount')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
